import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    kakao: any;
  }
}

export const SEOContent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const mapContainer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 카카오맵 초기화
    const initMap = () => {
      if (window.kakao && window.kakao.maps && mapContainer.current) {
        const options = {
          center: new window.kakao.maps.LatLng(35.15754, 129.06444), // 부산 부산진구 서전로 42
          level: 3 // 확대 레벨
        };

        const map = new window.kakao.maps.Map(mapContainer.current, options);

        // 마커 추가
        const markerPosition = new window.kakao.maps.LatLng(35.15754, 129.06444);
        const marker = new window.kakao.maps.Marker({
          position: markerPosition
        });
        marker.setMap(map);

        // 인포윈도우 추가
        const infowindow = new window.kakao.maps.InfoWindow({
          content: `<div style="padding:5px;font-size:12px;">${t('seoContent.branchName')}</div>`
        });
        infowindow.open(map, marker);
      }
    };

    // SDK 로드를 기다림
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkCount++;

      if (window.kakao && window.kakao.maps) {
        clearInterval(checkInterval);
        initMap();
      } else if (checkCount > 50) {
        clearInterval(checkInterval);
      }
    }, 100);

    return () => {
      clearInterval(checkInterval);
    };
  }, [i18n.language, t]);
  return (
    <div className="bg-gray-50">
      {/* Service Areas */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-4">{t('seoContent.serviceAreaTitle')}</h2>
          <p className="text-center text-gray-600 mb-12">
            {t('seoContent.serviceAreaDesc1')}<br />
            {t('seoContent.serviceAreaDesc2')}
          </p>
          <div className="flex justify-center gap-6">
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow w-64">
              <h3 className="text-xl font-semibold mb-3 text-blue-600">{t('seoContent.seomyeon')}</h3>
              <p className="text-gray-600 text-sm mb-2">{t('seoContent.seomyeonDesc')}</p>
              <p className="text-gray-500 text-xs">{t('seoContent.seomyeonAddress')}</p>
            </div>
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow w-64">
              <h3 className="text-xl font-semibold mb-3 text-blue-600">{t('seoContent.gaya')}</h3>
              <p className="text-gray-600 text-sm mb-2">{t('seoContent.gayaDesc')}</p>
              <p className="text-gray-500 text-xs">{t('seoContent.gayaAddress')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-12">{t('seoContent.benefitsTitle')}</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                ✓
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">{t('seoContent.benefitConvenientTitle')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t('seoContent.benefitConvenientDesc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                ✓
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">{t('seoContent.benefitInsuranceTitle')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t('seoContent.benefitInsuranceDesc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                ✓
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">{t('seoContent.benefitVehiclesTitle')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t('seoContent.benefitVehiclesDesc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                ✓
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">{t('seoContent.benefitSupportTitle')}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t('seoContent.benefitSupportDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-gray-50" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-12">{t('seoContent.faqTitle')}</h2>
          <div className="space-y-4">
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-lg font-semibold mb-3 text-gray-900" itemProp="name">
                {t('seoContent.faqReservationQ')}
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-gray-600 text-sm leading-relaxed" itemProp="text">
                  {t('seoContent.faqReservationA')}
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-lg font-semibold mb-3 text-gray-900" itemProp="name">
                {t('seoContent.faqPriceQ')}
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-gray-600 text-sm leading-relaxed" itemProp="text">
                  {t('seoContent.faqPriceA')}
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-lg font-semibold mb-3 text-gray-900" itemProp="name">
                {t('seoContent.faqPickupQ')}
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-gray-600 text-sm leading-relaxed" itemProp="text">
                  {t('seoContent.faqPickupA')}
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-lg font-semibold mb-3 text-gray-900" itemProp="name">
                {t('seoContent.faqInsuranceQ')}
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-gray-600 text-sm leading-relaxed" itemProp="text">
                  {t('seoContent.faqInsuranceA')}
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h3 className="text-lg font-semibold mb-3 text-gray-900" itemProp="name">
                {t('seoContent.faqShortTermQ')}
              </h3>
              <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <p className="text-gray-600 text-sm leading-relaxed" itemProp="text">
                  {t('seoContent.faqShortTermA')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-semibold text-gray-900 text-center mb-12">{t('seoContent.locationTitle')}</h2>
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">{t('seoContent.branchName')}</h3>
                <div className="space-y-3 text-gray-600 text-sm">
                  <p><strong>{t('seoContent.addressLabel')}:</strong> {t('seoContent.seomyeonAddress')}</p>
                  <p><strong>{t('seoContent.hoursLabel')}:</strong> {t('seoContent.hoursValue')}</p>
                  <p><strong>{t('seoContent.onlineReservationLabel')}:</strong> {t('seoContent.onlineReservationValue')}</p>
                  <p><strong>{t('seoContent.parkingLabel')}:</strong> {t('seoContent.parkingValue')}</p>
                </div>
                <div className="mt-6">
                  <h4 className="font-semibold text-base mb-2 text-gray-900">{t('seoContent.transportation')}</h4>
                  <ul className="space-y-1.5 text-gray-600 text-sm">
                    <li>{t('seoContent.subway')}</li>
                    <li>{t('seoContent.bus')}</li>
                  </ul>
                </div>
              </div>
              <div ref={mapContainer} className="rounded-xl h-64 w-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Keywords */}
      <section className="py-6 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs text-gray-500 leading-relaxed">
            {t('seoContent.keywords1')}<br />
            {t('seoContent.keywords2')}
          </p>
        </div>
      </section>
    </div>
  );
};
